/**
 * Auto-atualizacao do app desktop.
 *
 * A UI fica embutida no binario do Tauri, entao qualquer mudanca de interface
 * exigiria baixar o app de novo. Com o updater o app troca a si mesmo.
 *
 * Todo o codigo aqui e carregado por `import()` dinamico e so quando estamos
 * dentro do Tauri. O mesmo bundle serve o navegador, onde esses modulos nao
 * existem -- importar no topo do arquivo quebraria a versao web.
 */

export interface AvailableUpdate {
  version: string;
  notes: string | null;
  /** Baixa, instala e reinicia. Nao retorna se der certo. */
  install: (onProgress?: (percent: number | null) => void) => Promise<void>;
}

/** Estamos rodando dentro do shell Tauri (e nao no navegador)? */
export function isDesktopApp(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

/**
 * Procura uma versao nova. Devolve null quando nao ha nada, quando estamos no
 * navegador, ou quando a checagem falha -- atualizar e conveniencia, nunca
 * deve atrapalhar o uso do cockpit.
 */
export async function checkForUpdate(): Promise<AvailableUpdate | null> {
  if (!isDesktopApp()) return null;

  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    if (!update?.available) return null;

    return {
      version: update.version,
      notes: update.body ?? null,
      install: async (onProgress) => {
        let total = 0;
        let received = 0;

        await update.downloadAndInstall((event) => {
          // O total pode nao vir no cabecalho; nesse caso reportamos null e a
          // interface mostra progresso indeterminado em vez de mentir um %.
          if (event.event === 'Started') {
            total = event.data.contentLength ?? 0;
            onProgress?.(total > 0 ? 0 : null);
          } else if (event.event === 'Progress') {
            received += event.data.chunkLength;
            onProgress?.(total > 0 ? Math.min(100, Math.round((received / total) * 100)) : null);
          } else if (event.event === 'Finished') {
            onProgress?.(100);
          }
        });

        const { relaunch } = await import('@tauri-apps/plugin-process');
        await relaunch();
      },
    };
  } catch (err) {
    // Sem rede, manifesto fora do ar, assinatura invalida: tudo cai aqui e o
    // app segue funcionando na versao atual.
    console.warn('checagem de atualizacao falhou', err);
    return null;
  }
}
